import * as React from 'react';
import { AlertTriangle, Bell, Info, Package, Tag } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

import { Button } from '@dfq/shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@dfq/shared/components/ui/popover';
import { Skeleton } from '@dfq/shared/components/ui/skeleton';
import { cn } from '@dfq/shared/lib/utils';
import type {
  Notification,
  NotificationType,
} from '@dfq/shared/api/notifications';

/**
 * SH-NOTIFY-002 — Pure presentational notification bell + popover panel.
 *
 * Layering
 * --------
 * Per `.claude/rules/frontend-data-flow.md` the capsule renders only
 * what its parent passes via props and emits events through callbacks.
 * It does NOT import from `@dfq/shared/stores/*`, does NOT call
 * `useDispatch` / `useSelector` / `useNavigate`, and does NOT poll the
 * network. Those concerns live in the per-app `NotificationBellContainer`
 * mounted by `AdminLayout` (Console) and `CustomerLayout` (Storefront).
 *
 * Behavior implemented inside the capsule:
 *  - Bell button with an animated unread-count badge ("99+" cap, hidden
 *    when count is zero), `aria-label="Notifications"`, and
 *    `aria-expanded` reflecting the popover state.
 *  - Popover panel with header, list, and footer "View all" link. The
 *    panel closes on outside click and Escape (Radix Popover defaults).
 *  - Per-row type icon mapping (`order_status` → Package,
 *    `prepress_issue` → AlertTriangle, `quote_response` → Tag,
 *    `system` → Info), title, two-line truncated message body, relative
 *    timestamp ("2 hours ago"), and an unread indicator dot.
 *  - Empty state ("No notifications yet.") and a loading skeleton state.
 *
 * Behavior owned by the container:
 *  - `fetchUnreadCount` 60s polling (clears on unmount + on logout).
 *  - `fetchNotifications` dispatched on popover open (via `onOpenChange`).
 *  - Optimistic mark-read + mark-all-read (already in the slice; the
 *    container just dispatches the thunks).
 *  - Navigation to `notification.action_url` and `/account/notifications`.
 */

// ----------------------------------------------------------------------------
// Type → icon registry (registry pattern, not switch — follows the
// "registry, not hardcoded types" guidance from the layering rules).
// ----------------------------------------------------------------------------

interface NotificationTypeIconConfig {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Tailwind class applied to the icon wrapper, scoped to the type. */
  iconClassName: string;
  /** Screen-reader label for the icon (the visual icon is `aria-hidden`). */
  ariaLabel: string;
}

const NOTIFICATION_TYPE_ICONS: Record<NotificationType, NotificationTypeIconConfig> = {
  order_status: {
    Icon: Package,
    iconClassName: 'text-primary',
    ariaLabel: 'Order status notification',
  },
  prepress_issue: {
    Icon: AlertTriangle,
    iconClassName: 'text-destructive',
    ariaLabel: 'Prepress issue notification',
  },
  quote_response: {
    Icon: Tag,
    iconClassName: 'text-primary',
    ariaLabel: 'Quote response notification',
  },
  system: {
    Icon: Info,
    iconClassName: 'text-muted-foreground',
    ariaLabel: 'System notification',
  },
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

/**
 * Format an ISO 8601 timestamp into a "2 hours ago" relative string.
 * Returns an empty string if the input is not a parseable date so the
 * row layout never breaks on bad input.
 */
function relativeTime(iso: string): string {
  try {
    const parsed = parseISO(iso);
    if (Number.isNaN(parsed.getTime())) return '';
    return formatDistanceToNow(parsed, { addSuffix: true });
  } catch {
    return '';
  }
}

function clampUnread(count: number): string {
  if (count <= 0) return '0';
  if (count > 99) return '99+';
  return String(Math.floor(count));
}

// ----------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------

export interface NotificationBellProps {
  /** Notifications to render in the panel (newest first). */
  notifications: readonly Notification[];
  /** Total unread count from the slice. Drives the badge visibility and label. */
  unreadCount: number;
  /** When `true`, the panel renders a loading skeleton in place of the list. */
  isLoading?: boolean;
  /**
   * Controlled open state. Callers may omit it for the uncontrolled mode
   * (capsule manages its own open state internally).
   */
  open?: boolean;
  /**
   * Fires whenever the popover transitions between open and closed. The
   * container uses this to fire a `fetchNotifications` thunk on open.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Called when a notification row is clicked (or activated via Enter).
   * The container is expected to dispatch `markNotificationRead`, navigate
   * to `notification.action_url`, and close the popover.
   */
  onItemClick: (notification: Notification) => void;
  /**
   * Called when the "Mark all as read" button is clicked. The container
   * dispatches `markAllRead`.
   */
  onMarkAllRead: () => void;
  /**
   * Called when the "View all" footer link is clicked. When `undefined`,
   * the link is suppressed entirely (the admin context, where there is no
   * `/admin/notifications` route, passes `undefined`).
   */
  onViewAll?: () => void;
  /** Optional className for the bell trigger button. */
  className?: string;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export function NotificationBell({
  notifications,
  unreadCount,
  isLoading = false,
  open: controlledOpen,
  onOpenChange,
  onItemClick,
  onMarkAllRead,
  onViewAll,
  className,
}: NotificationBellProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const hasUnread = unreadCount > 0;
  const unreadLabel = clampUnread(unreadCount);
  const safeNotifications = notifications.slice(0, 10);
  const isEmpty = !isLoading && safeNotifications.length === 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          aria-expanded={open}
          aria-haspopup="dialog"
          data-testid="notification-bell-trigger"
          className={cn('relative', className)}
        >
          <Bell aria-hidden="true" />
          {hasUnread ? (
            <span
              data-testid="notification-bell-unread-badge"
              aria-label={`${unreadCount} unread notifications`}
              role="status"
              className={cn(
                'absolute top-0.5 right-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground',
                'transition-transform duration-200 ease-out',
                'animate-in zoom-in-75',
              )}
            >
              {unreadLabel}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0"
        data-testid="notification-bell-panel"
        role="dialog"
        aria-label="Notifications"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-sm font-semibold">Notifications</h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onMarkAllRead}
            disabled={!hasUnread || isLoading}
            data-testid="notification-bell-mark-all-read"
            className="h-auto px-2 py-1 text-xs"
          >
            Mark all as read
          </Button>
        </div>

        <div
          className="max-h-96 overflow-y-auto"
          data-testid="notification-bell-list"
        >
          {isLoading ? (
            <NotificationSkeletonList />
          ) : isEmpty ? (
            <p
              className="px-4 py-8 text-center text-sm text-muted-foreground"
              data-testid="notification-bell-empty"
            >
              No notifications yet.
            </p>
          ) : (
            <ul className="divide-y" role="list">
              {safeNotifications.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onClick={onItemClick}
                />
              ))}
            </ul>
          )}
        </div>

        {onViewAll !== undefined ? (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onViewAll}
              data-testid="notification-bell-view-all"
              className="w-full justify-center text-sm"
            >
              View all
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

// ----------------------------------------------------------------------------
// Subcomponents (internal)
// ----------------------------------------------------------------------------

interface NotificationRowProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
}

function NotificationRow({ notification, onClick }: NotificationRowProps) {
  const config =
    NOTIFICATION_TYPE_ICONS[notification.type] ??
    NOTIFICATION_TYPE_ICONS.system;
  const { Icon, iconClassName, ariaLabel } = config;
  const isUnread = notification.read_at === null;
  // Prefer `created_at` (present on the wire but optional per the slice's
  // type to preserve backwards compatibility); fall back to `read_at`
  // when the row has been read but lacks a creation timestamp.
  const timestampSource = notification.created_at ?? notification.read_at;
  const relative = timestampSource ? relativeTime(timestampSource) : '';

  const handleClick = React.useCallback(() => {
    onClick(notification);
  }, [notification, onClick]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLLIElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick(notification);
      }
    },
    [notification, onClick],
  );

  return (
    <li
      role="button"
      tabIndex={0}
      data-testid="notification-bell-row"
      data-notification-id={notification.id}
      data-unread={isUnread || undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors',
        'hover:bg-muted focus-visible:bg-muted focus-visible:outline-none',
        isUnread && 'bg-accent/40',
      )}
    >
      <span
        aria-label={ariaLabel}
        className={cn(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted',
          iconClassName,
        )}
      >
        <Icon aria-hidden="true" width={16} height={16} />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium">{notification.title}</p>
          {isUnread ? (
            <span
              data-testid="notification-bell-unread-dot"
              aria-label="Unread"
              className="ml-auto inline-block h-2 w-2 flex-shrink-0 rounded-full bg-primary"
            />
          ) : null}
        </div>
        <p className="line-clamp-2 text-xs text-muted-foreground">
          {notification.message}
        </p>
        {relative !== '' ? (
          <time className="text-[11px] text-muted-foreground">{relative}</time>
        ) : null}
      </div>
    </li>
  );
}

function NotificationSkeletonList() {
  return (
    <ul
      className="divide-y"
      role="list"
      data-testid="notification-bell-skeleton"
    >
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i} className="flex items-start gap-3 px-4 py-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-2 w-1/3" />
          </div>
        </li>
      ))}
    </ul>
  );
}
